import ModalManager from '@/utils/modal';
import { QuoteDevice, QuotePart, QuoteService, DeviceAttributeslItem, QuoteCustomerDeviceAttributes, QuoteImageItem, QuoteMode, QuoteState, AttributesInputTypes, JobChecklist, DeviceCheckList, CheckListDetailItem, PriceIngredient, ComputeData } from './../../../models/QuoteState';
import config from '@/config';
import { AppState } from '@/models/TechnicianAppState';
import { CustomerDevice, MultupleListItem } from '@/models/Common';
import { STATE_MODULES } from '@/store/state-modules';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { ActionContext } from 'vuex';
import { ConfigMutations } from '../config/mutations';
import { NOTIFICATION_TYPES, generateNotificationItem } from '@/models/AppConfig';
import QuoteUpdateSuccess from '@/components/modals/QuoteUpdateSuccess.vue';
import router from '@/router';
import { JobsActions } from '../jobs/actions';
import { CurrentJob, JobsContext } from '@/models/JobsState';
import { cloneDeep } from 'lodash';
import { QuoteMutations } from './mutations';
import { BaseTypeIds, QuoteServiceTypes } from '@/components/ui/jobs/JobDetails/CreateQuote/helpers';
import { JobsGetters } from '../jobs/getters';
import { services } from '@puls/puls-common-constants';

export const QuoteActions = {
	SUBMIT_QUOTE: 'SUBMIT_QUOTE',
	COMPUTE_QUOTE: 'COMPUTE_QUOTE'
}

export default {
	async [QuoteActions.SUBMIT_QUOTE] (context: ActionContext<QuoteState, AppState>) {
		const checklists: JobChecklist | null = cloneDeep(context.state.checklist);
		const params: any = {
			appointment_uuid: context.rootState.jobs.current_job?.uuid,
			images: [
				...context.state.jobImages, ...context.state.propertyImages
			].map((image: QuoteImageItem) => ({ url: image.url })),
			customer_devices_attributes: context.state.attributes.map((customerDeviceAttribute: QuoteCustomerDeviceAttributes) => {
				return {
					customer_device_id: customerDeviceAttribute.customer_device_id,
					attributes: customerDeviceAttribute.attributes?.map((attribute: DeviceAttributeslItem) => {
						return {
							device_attribute_id: attribute.device_attribute_id,
							value: getAttributeValue(attribute),
							images: getImagesFromState(attribute.images)
						}
					})
				}
			}),
			quotes: {}
		}

		if (checklists) {
			params.checklist = transformToServerChecklist(checklists)
		}

		if (context.state.mode === QuoteMode.IMAGES) {
			params.quotes.images = getImagesFromState(context.state.quoteImages).map((image: string) => ({ url: image }))
		}

		if (context.state.mode === QuoteMode.SERVICES) {
			const customerDevicesMap = context.rootGetters[`${STATE_MODULES.JOBS}/${JobsGetters.GET_CUSTOMER_DEVICES_MAP}`];
			params.quotes.devices = context.state.quoteDevices.map((quoteDevice: QuoteDevice) => {
				const deviceId = customerDevicesMap[quoteDevice.customerDeviceId]?.id;
				return {
					customer_device_id: quoteDevice.customerDeviceId,
					services: [
						...transfromToBEQuoteDeviceServices(deviceId, quoteDevice.services),
						...transfromToBEQuoteDeviceServices(deviceId, quoteDevice.parts, true)
					]
				}
			})
		}

		axios.post(`${config.api}/portal/appointment/send-job-quote`, params).then((result: AxiosResponse<any>) => {
			if (result.data.ok) {
				ModalManager.open(QuoteUpdateSuccess, {}, (result: any) => {
					if (result?.seeAll) {
						context.dispatch(`${STATE_MODULES.JOBS}/${JobsActions.CHANGE_JOB_CONTEXT}`, JobsContext[0], { root: true });
						router.instance.push('/jobs');
						return;
					}

					context.dispatch(`${STATE_MODULES.JOBS}/${JobsActions.LOAD_JOB_DETAILS}`, context.rootState.jobs.current_job?.uuid, { root: true });
				})
			} else {
				context.commit(
					`${STATE_MODULES.CONFIG}/${ConfigMutations.ADD_NOTIFICATION}`,
					generateNotificationItem({ type: NOTIFICATION_TYPES.error, message: result.data.message || "Cant update job!" }),
					{ root: true }
				);
			}
		}).catch((error: AxiosError) => {
			context.commit(
				`${STATE_MODULES.CONFIG}/${ConfigMutations.ADD_NOTIFICATION}`,
				generateNotificationItem({ type: NOTIFICATION_TYPES.error, message: error.message || "Something wnet wrong!" }),
				{ root: true }
			);
		})
	},
	async [QuoteActions.COMPUTE_QUOTE] (context: ActionContext<QuoteState, AppState>) {
		try {
			if (context.state.mode === QuoteMode.IMAGES) {
				return context.commit(`${STATE_MODULES.QUOTE}/${QuoteMutations.TOGGLE_EDIT_MODE}`, {}, { root: true })
			}
	
			const currentJob: CurrentJob = context.rootGetters[`${STATE_MODULES.JOBS}/${JobsGetters.GET_CURRENT_JOB}`];
			const customerDevicesMap = context.rootGetters[`${STATE_MODULES.JOBS}/${JobsGetters.GET_CUSTOMER_DEVICES_MAP}`];
			const ingredients = getIngredients(context.state.quoteDevices, customerDevicesMap);
				
			const calculateParams: any = {
				context: 'property_manager_quote',
				marketId: currentJob?.contact.market_id,
				type: 'withTaxes',
				ingredients,
				zipcode: currentJob?.contact.zipcode
			}
	
			if (currentJob?.contact.subscription_plan_id) { 
				calculateParams.subscription_plan_id = currentJob?.contact.subscription_plan_id;
			}
	
			const { data } = await axios.post(`${config.api}/portal/price/calculate`, calculateParams);
			if (!data) throw new Error('Cant calculate prices');
			const payload: ComputeData = {
				totalPrice: data.totalPrice,
				tax: data.taxPrice,
				ingredients: (data.priceIngredients || []).map((ing: any) => { 
					const service: PriceIngredient = { 
						serviceId: ing.serviceId,
						deviceId: ing.deviceId,
						customerDeviceId: ing.customerDeviceId,
						title: ing.serviceDisplayTitle,
						price: ing.totalPrice,
						isAutoAdded: !!ing.is_auto_added,
						baseTypeId: ing.baseTypeId
					}
	
					if (ing.partSku) {
						service.sku = ing.partSku
					}
	
					return service;
				})
			}
	
			context.commit(`${STATE_MODULES.QUOTE}/${QuoteMutations.UPDATE_CALCULATE_DATA}`, payload, { root: true });
			context.commit(`${STATE_MODULES.QUOTE}/${QuoteMutations.TOGGLE_EDIT_MODE}`, {}, { root: true })
		} catch (error: any) {
			context.commit(
				`${STATE_MODULES.CONFIG}/${ConfigMutations.ADD_NOTIFICATION}`,
				generateNotificationItem({ type: NOTIFICATION_TYPES.error, message: error.message || "Something went wrong!" }),
				{ root: true }
			);
		}
	}
}

function getImagesFromState (images: QuoteImageItem[] | string []) : string[] {
	return images.map((img: QuoteImageItem | string) => {
		if (typeof img === 'string') {
			return img
		}
		
		return img.url;
	})
}

function transfromToBEQuoteDeviceServices (deviceId: number, list: QuotePart[] | QuoteService[], withSku = false) {
	return list.map((item: QuotePart | QuoteService) => {
		const price = parseFloat(item.price)
		const obj: any = {
			id: item.id,
			payment: price,
			price: price,
			title: item.title,
			device_id: deviceId
		}

		if (withSku) {
			const { sku } = item as QuotePart
			obj.sku = sku
			obj.part_sku = sku
			obj.part_tech_price = price
		}

		return obj;
	})
}

function getAttributeValue (attribute: DeviceAttributeslItem) {
	switch (attribute.type) {
	case AttributesInputTypes.FREE_TEXT:
	case AttributesInputTypes.SINGLE_SELECTION:
		return attribute.value;
	case AttributesInputTypes.MULTIPLE_SELECTION:
		return getSelectedOptions(attribute.options)
	default:
		break;
	}
}

function getSelectedOptions (options: MultupleListItem[] | string[]) : string {
	return (
		options
			.filter((option: MultupleListItem | string) => (option as MultupleListItem).selected)
			.map((option: MultupleListItem | string) => (option as MultupleListItem).title)
			.join(', ')
	)
}

function transformToServerChecklist (checklist: JobChecklist) {
	function travers (checklist: DeviceCheckList[]) {
		checklist.forEach((deviceChecklist: DeviceCheckList) => {
			deviceChecklist.checklist_details.forEach((item: CheckListDetailItem) => {
				item.images = transformImages(item.images as QuoteImageItem[]);
			})
		})
	}

	travers(checklist.post)
	travers(checklist.pre)

	return checklist
}

function transformImages (images: QuoteImageItem[]) {
	return images.map((item: QuoteImageItem) => item.url);
}

const getIngredients = (devices: QuoteDevice[], customerDevicesMap: {[key: string]: CustomerDevice}) => { 
	return devices.map((device: QuoteDevice) => {
		const { customerDeviceId, services, parts } = device;
		const deviceId = customerDevicesMap[customerDeviceId].id;
		const servicesToCompute = createComputeIngredient(services, customerDeviceId, QuoteServiceTypes.SERVICE);
		const partsToCompute = createComputeIngredient(parts, customerDeviceId, QuoteServiceTypes.PART)
		return { 
			customerDeviceId,
			deviceId,
			services: [...servicesToCompute, ...partsToCompute]
		}
	});
}

const createComputeIngredient = (items: QuotePart[] | QuoteService[], customerDeviceId: number, type: string) => {
	return items.map((s: QuoteService | QuotePart) => {
		const service: any = { 
			customer_device_id: customerDeviceId,
			id: s.id,
			base_type_id: BaseTypeIds[type]
		}
		
		if (s.id === services.GENERIC_REPAIR_SERVICE) {
			service.payment = s.price;
			service.price = s.price;
			service.title = s.title;
		}

		if (type === QuoteServiceTypes.PART) { 
			service.part_tech_price = s.price;
			service.part_sku = (s as QuotePart).sku;
			service.title = s.title;
		}
		
		return service;
	})
}
